import { Alert, Snackbar } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { useCase, useDeleteCase } from '../../../../shared/hooks/caseHook';
import { FilterSearchBar } from '../../common/components/FilterSearchBar';
import { CaseFilter } from '../../common/components/FilterSearchBar/SearchModal';
import {
  useSelectedIndices,
  useServerSideSelected,
} from '../../common/components/SortableTable';
import {
  CurrentTimeText,
  ImageListTop,
  SectionContentContainer,
  SectionHeading,
} from '../../common/components/styledComponents';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { DeleteConfirmModal } from './DeleteConfirmModal';
import { ImageDetails } from './ImageDetails';
import { ImageListTable } from './ImageListTable';
import { ImageListTableButtons } from './ImageListTableButtons';
import { RequestCdModal } from './RequestCdModal';

const TableTopContainerWrapper = styled.div`
  margin-left: auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 15px;
  row-gap: 8px;
`;

const DEFAULT_COUNT_PER_PAGE_OPTIONS = [5, 10, 25];

export const ImageList = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.imageList',
  });
  const { t: globalT } = useTranslation('translation');

  const currentTime = moment().format(`${globalT('currentTimeWithoutLabel')}`);

  // 검색
  const [filter, setFilter] = useState<CaseFilter | undefined>();
  // 테이블
  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(
    DEFAULT_COUNT_PER_PAGE_OPTIONS[0],
  );
  const {
    data: caseResponse,
    isFetching: isFetchingCases,
    refetch: refetchCase,
  } = useCase({ filter, page, size: countPerPage });
  const cases = caseResponse?.content ?? [];
  const totalCount = caseResponse?.totalElements;

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const { selected, selectedData, selectOne, selectAll, unselectAll } =
    useServerSideSelected(cases, 'caseId');
  const [focused, setFocused] = useState<Case | null>(null);

  const refreshCase = () => {
    refetchCase();
    unselectAll();
    unselect();
    setFocused(null);
  };

  const {
    selectIndex,
    selectAllIndicesInCurrentPage,
    unselect,
    handlePaginationAfterDelete,
  } = useSelectedIndices();
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const onSuccessDeleteCases = async () => {
    setOpenSnackbar(true);
    setFocused(null);
    unselectAll();
    const { data: refetchCaseResponse } = await refetchCase();
    if (page && refetchCaseResponse && selected) {
      handlePaginationAfterDelete(
        page,
        countPerPage,
        cases.length,
        handleChangePage,
      );
    }
  };
  const { mutate: deleteCase, isError } = useDeleteCase({
    onSuccess: onSuccessDeleteCases,
  });
  const onClickDeleteButton = useCallback(() => {
    deleteCase(selected as string[]);
    handlePaginationAfterDelete(
      page,
      countPerPage,
      cases.length,
      handleChangePage,
    );
    setOpenDeleteConfirmModal(false);
  }, [
    cases.length,
    countPerPage,
    deleteCase,
    handlePaginationAfterDelete,
    page,
    selected,
  ]);

  const [openCDModal, setOpenCDModal] = useState(false);

  const getIndex = (id: string) => {
    return page * countPerPage + cases.findIndex(c => c.caseId === id);
  };

  const handleSelectAll = (checked: boolean, selectables: Case[]) => {
    selectAll(checked, selectables);
    selectAllIndicesInCurrentPage(
      Array.from(
        { length: countPerPage },
        (_, index) => page * countPerPage + index,
      ),
      checked,
    );

    if (!checked) {
      setFocused(null);
    }
  };
  const handleFocused = (newFocused: Case) => {
    setFocused(newFocused);
  };
  const handleSelect = (id: string, checked: boolean) => {
    selectOne(id);
    selectIndex(getIndex(id), checked);

    if (checked) {
      const selectedCase = cases.find(({ caseId }) => caseId === id) as Case;
      setFocused(selectedCase);
    } else if (focused?.caseId === id) {
      setFocused(null);
    }
  };

  const windowSize = useContext(WindowSizeContext);

  const handleSearchFilter = (searchFilter?: CaseFilter) => {
    setPage(0);
    setFilter(searchFilter);
    selectAll(false);
    unselect();
    setFocused(null);
  };

  const onClickDeleteImagesButton = () => {
    if (selected.length) {
      setOpenDeleteConfirmModal(true);
    }
  };

  const onClickCDButton = () => {
    if (!selected.length) {
      return;
    }
    setOpenCDModal(true);
  };

  return (
    <>
      <SectionContentContainer>
        <SectionHeading>{t('myImageList.heading')}</SectionHeading>
        <ImageListTop
          left={<FilterSearchBar onSearch={handleSearchFilter} />}
          right={
            <>
              <TableTopContainerWrapper>
                <CurrentTimeText>{currentTime}</CurrentTimeText>
                <ImageListTableButtons
                  onClickCDButton={onClickCDButton}
                  caseIds={selected as string[]}
                  refreshCase={refreshCase}
                  onClickDeleteImagesButton={onClickDeleteImagesButton}
                />
              </TableTopContainerWrapper>
            </>
          }
        />
        <ImageListTable
          dataToShow={cases}
          totalCount={totalCount}
          countPerPage={countPerPage}
          countPerPageOptions={DEFAULT_COUNT_PER_PAGE_OPTIONS}
          page={page}
          onChangePage={handleChangePage}
          onChangeCountPerPage={newCountPerPage => {
            setPage(0);
            setCountPerPage(newCountPerPage);
          }}
          data={cases}
          selected={selected}
          isSelectable={() => true}
          onSelect={handleSelect}
          onSelectAll={handleSelectAll}
          focused={focused}
          onFocusData={handleFocused}
          isLoading={isFetchingCases}
        />
      </SectionContentContainer>
      {focused && windowSize !== WindowSize.MOBILE && (
        <ImageDetails caseInfo={focused} />
      )}
      <DeleteConfirmModal
        onClickDeleteButton={onClickDeleteButton}
        open={openDeleteConfirmModal}
        onClose={() => setOpenDeleteConfirmModal(false)}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}>
        <Alert severity={isError ? 'error' : 'success'} sx={{ minWidth: 300 }}>
          {isError
            ? `${t('myImageList.deleteButton.message.fail')}`
            : `${t('myImageList.deleteButton.message.success')}`}
        </Alert>
      </Snackbar>
      <RequestCdModal
        open={openCDModal}
        onClose={() => setOpenCDModal(false)}
        cases={selectedData}
      />
    </>
  );
};
